import $ from 'jquery';
import wretch from 'wretch';
import AccountService from '../_shared/account-service';
import { sleepFor } from '../_shared/helpers';
/**
 * @desc Implements autoplay video in brightcove player
 * */
export default class BrightcovePlayer {
  /**
   * @param {string} playerSelector
   * @param {string} premiumPlayerId
   */
  constructor(playerSelector, premiumPlayerId) {
    this.$player = $(playerSelector);
    this.playerId = this.$player.attr('id');
    this.videoId = this.$player.data('video-id');
    this.isPremium = this.playerId === premiumPlayerId;
    this.autoplay = !!+this.$player.data('autoplay');
    this.volumeLevel = 0.5;
    this.player = null;
    this.apiURL = `${window.location.origin}/web3/api/brightcove/playheads/`;

    this.init();
  }

  async init() {
    this.isInitialized = true;

    if (!this.$player.length || !this.playerId) {
      this.isInitialized = false;

      return;
    }

    this.player = window.videojs.getPlayer(this.playerId);

    if (this.autoplay) {
      this.initAutoplay();
    }

    const authToken = AccountService.getAuthToken();

    if (!authToken) return;

    const userData = await AccountService.getUserData();

    this.player.ready(function () {
      const player = this;

      // Set the viewer id for Brightcove analytics
      if (userData) {
        player.bcAnalytics.client.setUser(userData.id);
      }
    });

    try {
      await this.postResumeTime(userData.id);

      if (this.isPremium) {
        /* try two times to get video id for premium video */
        if (!this.videoId) {
          await sleepFor(2000);
          this.videoId = this.$player.attr('data-video-id');
        }
        if (!this.videoId) {
          await sleepFor(2000);
          this.videoId = this.$player.attr('data-video-id');
        }
      }

      setTimeout(() => this.getResumeTime(userData.id, 1), 1000);
    } catch (e) {
      console.error('An error occurred during fetching time for brightcove player: ', e);
    }
  }

  /**
   * @desc Init autoplay for Brightcove player
   */
  initAutoplay() {
    try {
      /* Generally, when using iOS, Safari or Chrome, a video will NOT autoplay if the video is not muted. */
      this.player.on('loadedmetadata', () => {
        const promise = this.player.play();

        if (promise !== undefined) {
          promise.then(() => {
            /* Autoplay started */
            this.player.muted(false);
            this.player.volume(this.volumeLevel);

            if (this.player.paused()) {
              this.player.muted(true);
              this.player.play();
            }
          })
            .catch(() => {
              /* Autoplay was prevented */
              this.player.muted(true);
              this.player.play();
            });
        }
      });
    } catch (e) {
      console.error('Error:', e.error, 'Details:', e.details);
    }
  }

  /**
   * @param {string} userId
   * @returns {Promise<object>}
   */
  fetchResumeTime(userId) {
    return wretch(`${this.apiURL}${userId}`)
      .options({ credentials: 'same-origin' })
      .get()
      .json()
      .catch(() => null);
  }

  /**
   * @desc set resume time for Brightcove player
   */
  setBrightcoveResumeTime(viewerPlayheadsResponse) {
    const currentPlayheads = viewerPlayheadsResponse.items.find((item) => +item.video_id === +this.videoId);

    if (currentPlayheads) {
      this.player.currentTime(currentPlayheads.playhead_seconds);
    }
  }

  /**
   * @param {string} userId
   * @returns {Promise<string>}
   */
  postResumeTime(userId) {
    return wretch(`${this.apiURL}${userId}`)
      .options({ credentials: 'same-origin' })
      .post()
      .text();
  }

  /**
  * @param {string} userId
  * @param {number} index
  * @desc try to fetch Cross-Device Resume for Brightcove plauer up to 3 times
  */
  async getResumeTime(userId, index) {
    if (index > 3) return false;

    const viewerPlayheadsResponse = await this.fetchResumeTime(userId);

    if (viewerPlayheadsResponse) {
      this.setBrightcoveResumeTime(viewerPlayheadsResponse);
    } else {
      setTimeout(() => this.getResumeTime(userId, index + 1), index * 1000);
    }

    return true;
  }
}
